import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { parseWallClockTime } from '../../../shared/Helpers/timezones';

import { errorNormalizer } from '../../../shared/Helpers/functions';
import IntroCallData from './introCallData';
import { useCanEditSOAPNote, usePostSubmitEditMode } from '../helpers';
import { ACTIONS, initialIntroCallState, introCallReducer } from './introCallReducer';
import { useResetCalendarData } from '../../Calendar/Helpers/hooks';
import { CustomButton } from '../../../shared/uiComponents';
import { ButtonWrapper } from '../../../components/StyledComponents';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getSoapNote, updateIntroCall } from '../../../redux/State/clientSlice/soapNoteSlice';
import { setDate, setEndTime, setStartTime } from '../../../redux/State/clientSlice/sessionSlice';
import { IntroCallProperties, UpdateIntroCallProperties } from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';

// The intro call is expected to be short; this is a soft hint only, not a hard limit.
const SOFT_DURATION_CAP_MINUTES = 15;

const IntroCall = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const { resetData } = useResetCalendarData();
	const canEdit = useCanEditSOAPNote();
	const { isPostSubmitEditMode } = usePostSubmitEditMode();

	const reportId = params.get('reportId');
	const clientId = params.get('clientId');

	const sessionInfo = useSelector((state) => state.session.sessionInfo);
	const isUpdating = useSelector((state) => state.soapNote.isUpdating);

	const [data, setData] = useReducer(introCallReducer, initialIntroCallState);
	const currentUpdateRequestRef = useRef<ReturnType<typeof dispatch<any>> | null>(null);
	const isMounted = useRef(true);

	useEffect(() => {
		isMounted.current = true;

		return () => {
			isMounted.current = false;

			if (currentUpdateRequestRef.current) {
				currentUpdateRequestRef.current.abort();
			}
		};
	}, []);

	useEffect(() => {
		if (!reportId || !clientId) return;

		dispatch(getSoapNote({ reportId, clientId }))
			.then(unwrapResult)
			.then((response) => {
				const { date, startTime, endTime } = response as IntroCallProperties;

				dispatch(setDate(date));
				dispatch(setStartTime(startTime));
				dispatch(setEndTime(endTime));

				setData({
					type: ACTIONS.setState,
					payload: response,
				});
			})
			.catch((error) => {
				errorNormalizer(error);
				if (error.status === 403) navigate('/dashboard');
			});
	}, [dispatch, clientId, reportId, navigate]);

	const saveHandler = useCallback(() => {
		if (currentUpdateRequestRef.current) {
			currentUpdateRequestRef.current.abort();
		}
		if (!reportId || !clientId || !data) return;
		const { date, startTime, endTime } = sessionInfo;

		const durationMinutes = parseWallClockTime(endTime).diff(parseWallClockTime(startTime), 'minute');
		if (durationMinutes > SOFT_DURATION_CAP_MINUTES) {
			toast.warning(
				`This call is longer than the typical ${SOFT_DURATION_CAP_MINUTES} minutes for an Intro Call. Please double-check the times.`,
			);
		}

		const allData: UpdateIntroCallProperties = {
			...data,
			date,
			startTime,
			endTime,
			reportId,
			clientId,
		};
		const requestPromise = dispatch(updateIntroCall(allData));
		currentUpdateRequestRef.current = requestPromise;

		requestPromise
			.then(unwrapResult)
			.then(() => {
				if (!isMounted.current) return;
				resetData();
				toast('Saved');
				navigate('/calendar');
			})
			.catch(errorNormalizer)
			.finally(() => {
				if (currentUpdateRequestRef.current === requestPromise) {
					currentUpdateRequestRef.current = null;
				}
			});
	}, [reportId, clientId, data, sessionInfo, navigate, resetData, dispatch]);

	return (
		<>
			<IntroCallData setData={setData} data={data} />
			{(canEdit || isPostSubmitEditMode) && (
				<ButtonWrapper>
					<CustomButton loading={isUpdating} title="Save" className="marginTop8" onClick={saveHandler} />
				</ButtonWrapper>
			)}
		</>
	);
};

export default IntroCall;
